<template>
  <section class="content">
    <div class="container-fluid">
      <b-row class="col-lg-12">
        <b-form-group class="col-lg-12 col-md-12 col-sm-12">
          <PlayerVipLimitForm
            :create="true"
            title="Create player VIP limit"
            @finish="handleItemsUpdate"
          >
            <template #button="{ onclick }">
              <b-button
                type="button"
                class="mr-1"
                variant="success"
                @click="onclick"
              >
                <i class="fas fa-plus" /> Create
              </b-button>
            </template>
          </PlayerVipLimitForm>
        </b-form-group>
      </b-row>

      <b-row class="col-lg-12 mb-3">
        <b-col lg="4" md="6" sm="12">
          <b-form-group label="Uid" label-for="player-uid-filter">
            <b-form-input
              id="player-uid-filter"
              v-model="filters.uid"
              autocomplete="off"
              placeholder="Search UID"
              @keyup.enter="loadItems"
            />
          </b-form-group>
        </b-col>
        <b-col lg="4" md="6" sm="12">
          <b-form-group label="Operator" label-for="player-vip-filter-operator">
            <multiselect
              id="player-vip-filter-operator"
              v-model="filters.operatorId"
              :options="operatorFilterOptions"
              label="label"
              track-by="value"
              :multiple="false"
              placeholder="Select"
              select-label="Select"
              deselect-label="Remove"
              selected-label="Selected"
              :disabled="
                this.$store.getters['limits/operatorsForSelect'] === null
              "
              :clear-on-select="false"
              :close-on-select="true"
              :show-labels="true"
              :allow-empty="true"
              :clearable="true"
              :preserve-search="true"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row class="col-lg-12 mb-3">
        <b-col lg="4" md="6" sm="12">
          <b-form-group label="Table" label-for="player-vip-filter-table">
            <multiselect
              id="player-vip-filter-table"
              v-model="filters.tableId"
              :options="getTablesOptions()"
              :custom-label="
                opt =>
                  this.$store.getters['isShowDebugTools']
                    ? opt.value
                    : opt.value + ' - ' + opt.label
              "
              track-by="value"
              :multiple="false"
              placeholder="Select"
              select-label="Select"
              deselect-label="Remove"
              selected-label="Selected"
              :disabled="
                this.$store.getters['tablesForSelect/list'] === null
              "
              :clear-on-select="false"
              :close-on-select="true"
              :show-labels="true"
              :allow-empty="true"
              :clearable="true"
              :preserve-search="true"
            />
          </b-form-group>
        </b-col>
        <b-col lg="4" md="6" sm="12">
          <b-form-group label="Currency" label-for="player-vip-filter-currency">
            <multiselect
              id="player-vip-filter-currency"
              v-model="filters.currency"
              :options="
                this.$store.getters['limits/searchCurrenciesForSelect'](
                  currenciesQuery
                ) || []
              "
              :custom-label="
                opt =>
                  this.$store.getters['isShowDebugTools']
                    ? opt.value
                    : opt.label
              "
              track-by="value"
              :multiple="false"
              placeholder="Select"
              select-label="Select"
              deselect-label="Remove"
              selected-label="Selected"
              :disabled="
                this.$store.getters['limits/currenciesForSelect'] === null
              "
              :internal-search="false"
              @search-change="query => (currenciesQuery = query)"
              :clear-on-select="false"
              :close-on-select="true"
              :show-labels="true"
              :allow-empty="true"
              :clearable="true"
              :preserve-search="true"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row class="col-lg-12 mb-3">
        <b-col cols="12" class="d-flex align-items-end">
          <FilterButtons
            :cancel="cancel"
            :loadFn="loadItems"
            :cancelFn="cancelRequest"
            :resetFn="clearFilters"
          />
        </b-col>
      </b-row>

      <b-row class="col-lg-12">
        <div class="col-lg-12">
          <div class="table-wrapper mb-10">
            <b-table
              :striped="true"
              :small="true"
              :hover="true"
              show-empty
              :empty-text="emptyText"
              :sort-by="sortBy"
              :sort-desc="sortDesc"
              :busy="!!cancel"
              :items="items"
              :fields="fields"
              :no-local-sorting="true"
              :sort-icon-left="true"
              @sort-changed="sortingChanged"
            >
              <template #cell(operatorId)="row">
                <OperatorLabel :value="row.item.operatorId" />
              </template>
              <template #cell(tableId)="row">
                <TableName :tableId="row.item.tableId" />
              </template>
              <template #cell(currency)="row">
                <span>{{ row.item.currency || '-' }}</span>
              </template>
              <template #cell(vipLimitType)="row">
                <span class="badge badge-success">
                  {{ getLimitTypeDisplay(row.item.vipLimitType) }}
                </span>
              </template>
              <template #cell(actions)="row">
                <b-button
                  variant="info"
                  size="xs"
                  class="mr-1"
                  @click="goToTableLimit(row)"
                >
                  GoToLimit
                </b-button>
                <PlayerVipLimitForm
                  :create="false"
                  :defaultValues="{
                    uid: row.item.uid,
                    operatorId: row.item.operatorId,
                    tableId: row.item.tableId,
                    vipLimitType: row.item.vipLimitType
                  }"
                  title="Edit player VIP limit"
                  @finish="handleItemsUpdate"
                />
                <RemoveForm
                  :object="`${row.item.uid}/${row.item.operatorId}/${row.item.tableId}`"
                  message="Please confirm removing player VIP limit"
                  :url="`/api/v1/player-vip-limits/${row.item.uid}/${row.item.operatorId}/${row.item.tableId}`"
                  @finish="handleItemsUpdate"
                />
              </template>
            </b-table>
          </div>
        </div>
      </b-row>

      <b-row class="row mb-10">
        <div class="col-12">
          <b-pagination
            class="float-right"
            v-model="pagination.page"
            :total-rows="pagination.records"
            :per-page="pagination.onPage"
            @change="onPageChange"
            first-number
            last-number
          />
        </div>
      </b-row>

      <b-row class="row mt-10 mb-10">
        <div class="col-12">
          <ve-pagination
            class="float-right"
            :page-index="pagination.page"
            :page-size="pagination.onPage"
            :total="pagination.records"
            :pagingCount="pagination.count"
            :layout="['total', 'sizer']"
            :pageSizeOption="pagination.pageSizeOption"
            @on-page-size-change="onPageSizeChange"
          />
        </div>
      </b-row>

      <EditLimitForm
        ref="goToLimitForm"
        :key="goToLimitKey"
        :default-values="goToLimitDefaults"
        title="Edit limit"
        @finish="handleItemsUpdate"
      >
        <template #button><span /></template>
      </EditLimitForm>
    </div>
  </section>
</template>

<script>
import axios from 'axios'
import Multiselect from 'vue-multiselect'
import EditLimitForm from '../../components/Limits/EditLimitForm.vue'
import OperatorLabel from '../../components/Limits/OperatorLabel.vue'
import TableName from '../../components/Limits/TableName.vue'
import PlayerVipLimitForm from '../../components/Limits/PlayerVipLimitForm.vue'
import RemoveForm from '../../components/remove-form/RemoveForm.vue'
import FilterButtons from '../../components/FilterButtons'
import {
  LIMIT_TYPES,
  getLimitTypeText
} from '@/constants/LimitTypes'

function getDefaultPagination() {
  return {
    onPage: 10,
    page: 1,
    records: 0,
    count: 0,
    pageSizeOption: [10, 25, 50, 100, 200]
  }
}

function getDefaultFilters() {
  return {
    uid: null,
    operatorId: null,
    tableId: null,
    currency: null
  }
}

export default {
  components: {
    Multiselect,
    EditLimitForm,
    OperatorLabel,
    TableName,
    PlayerVipLimitForm,
    RemoveForm,
    FilterButtons
  },
  data() {
    return {
      cancel: null,
      error: null,
      currenciesQuery: '',
      goToLimitDefaults: {},
      goToLimitKey: 0,
      filters: getDefaultFilters(),
      fields: [
        { key: 'uid', label: 'Uid', sortable: true },
        { key: 'operatorId', label: 'Operator', sortable: false },
        { key: 'tableId', label: 'Table', sortable: false },
        { key: 'currency', label: 'Currency', sortable: false },
        { key: 'vipLimitType', label: 'Vip-Limit', sortable: false },
        { key: 'actions', label: 'Actions' }
      ],
      items: [],
      sortBy: null,
      sortDesc: null,
      pagination: getDefaultPagination()
    }
  },
  computed: {
    emptyText() {
      if (this.cancel) {
        return 'Loading items..'
      }
      if (this.error) {
        return this.error
      }
      return 'There are no records to show'
    },
    limitTypeOptions() {
      return LIMIT_TYPES
    },
    operatorFilterOptions() {
      return this.$store.getters['limits/operatorsForSelect'] || []
    }
  },
  methods: {
    getTablesOptions() {
      return this.$store.getters['tablesForSelect/list'] || []
    },
    async goToTableLimit(row) {
      const item = row.item
      if (!item.currency) {
        this.$toastr.w('Лимит отсутствует')
        return
      }

      try {
        const resp = await this.$http({
          url: '/api/v1/limits',
          method: 'GET',
          params: {
            partnerId: item.operatorId,
            tableId: String(item.tableId),
            currency: item.currency,
            limitType: item.vipLimitType,
            limit: 1,
            offset: 0
          }
        })

        const items = resp.data?.items || []
        if (!items.length) {
          this.$toastr.w('Лимит отсутствует')
          return
        }

        this.goToLimitDefaults = { ...items[0] }
        this.goToLimitKey += 1
        await this.$nextTick()
        if (this.$refs.goToLimitForm) {
          this.$refs.goToLimitForm.toggle()
        }
      } catch (err) {
        this.$toastr.e(
          JSON.stringify(err?.response?.data?.message || err?.message || err)
        )
      }
    },
    getLimitTypeDisplay(limitType) {
      return getLimitTypeText(limitType)
    },
    handleItemsUpdate() {
      this.loadItems()
    },
    clearFilters() {
      this.filters = getDefaultFilters()
      this.sortBy = null
      this.sortDesc = null
      this.pagination.page = 1
      this.loadItems()
    },
    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.pagination.page = 1
      this.loadItems()
    },
    cancelRequest() {
      if (this.cancel) {
        this.cancel('cancel request')
      }
    },
    onPageChange(page) {
      this.pagination.page = page
      this.loadItems()
    },
    onPageSizeChange(pageSize) {
      this.pagination.page = 1
      this.pagination.onPage = pageSize
      this.loadItems()
    },
    loadItems() {
      this.items = []

      const params = {
        limit: this.pagination.onPage,
        offset: this.pagination.onPage * (this.pagination.page - 1),
        direction: this.sortDesc ? 'DESC' : 'ASC'
      }

      if (this.filters.uid) {
        params.uid = this.filters.uid
      }

      if (this.filters.operatorId?.value) {
        params.operatorId = this.filters.operatorId.value
      }

      if (this.filters.tableId?.value) {
        params.tableId = this.filters.tableId.value
      }

      if (this.filters.currency?.value) {
        params.currency = this.filters.currency.value
      }

      if (this.sortBy) {
        params.orderBy = this.sortBy
      } else {
        params.orderBy = 'uid'
      }

      this.$http({
        url: '/api/v1/player-vip-limits',
        params: params,
        method: 'GET',
        cancelToken: new axios.CancelToken(c => {
          this.cancel = c
        })
      })
        .then(resp => {
          const {
            items,
            pagination: { limit, totalCount }
          } = resp.data
          this.items = items
          this.pagination.onPage = limit
          this.pagination.records = totalCount
        })
        .catch(err => {
          this.items = []
          this.error = err
        })
        .finally(() => {
          this.cancel = null
        })
    }
  },
  async beforeMount() {
    await this.$store.dispatch('tablesForSelect/loadList').catch(() => {})
    await this.$store.dispatch('limits/loadOperatorsForSelect').catch(() => {})
    await this.$store.dispatch('limits/ensureLoadCurrenciesForSelect').catch(
      () => {}
    )
  },
  mounted() {
    this.loadItems()
  }
}
</script>

